import React, { useEffect, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Save,
  X,
  User,
  Mail,
  Phone,
  Building,
  CheckCircle,
  XCircle,
  Eye,
  Calendar,
  Shield
} from 'lucide-react';
import { authAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';

const defaultForm = {
  name: '',
  email: '',
  password: '',
  role: '',
  phone: '',
  store_id: '',
  is_active: true,
};

const Employes = () => {
  const [employees, setEmployees] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch employees
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await authAPI.getEmployees();
      setEmployees(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des employés');
    }
    setLoading(false);
  };

  // Fetch stores for dropdown
  const fetchStores = async () => {
    try {
      const res = await authAPI.getStores();
      setStores(res.data);
    } catch (err) {
      console.error('Error fetching stores:', err);
      toast.error('Erreur lors du chargement des magasins');
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchStores();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        // For updates, only include password if it's not empty
        const updateData = { ...form };
        if (!updateData.password) {
          delete updateData.password;
        }
        await authAPI.updateEmployee(editingId, updateData);
        toast.success('Employé modifié');
      } else {
        await authAPI.createEmployee(form);
        toast.success('Employé ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchEmployees();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle view employee
  const handleView = async (emp) => {
    try {
      const res = await authAPI.getEmployee(emp.id);
      setSelectedEmployee(res.data);
      setShowViewModal(true);
    } catch (err) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Handle edit
  const handleEdit = async (emp) => {
    try {
      const res = await authAPI.getEmployee(emp.id);
      const employeeData = res.data;
      setForm({
        name: employeeData.name,
        email: employeeData.email,
        password: employeeData.password_display || '', // Show current password
        role: employeeData.role,
        phone: employeeData.phone || '',
        store_id: employeeData.store_id || '',
        is_active: employeeData.is_active,
      });
      setEditingId(employeeData.id);
      setShowForm(true);
    } catch (err) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet employé ?')) return;
    try {
      await authAPI.deleteEmployee(id);
      toast.success('Employé supprimé');
      fetchEmployees();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const filteredEmployees = employees.filter(employee =>
    String(employee.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(employee.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(employee.role || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'danger',
      manager: 'warning',
      employee: 'primary'
    };
    return badges[role] || 'neutral';
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <ModernBadge variant="success" icon={<CheckCircle className="w-3 h-3" />}>
        Actif
      </ModernBadge>
    ) : (
      <ModernBadge variant="danger" icon={<XCircle className="w-3 h-3" />}>
        Inactif
      </ModernBadge>
    );
  };

  const roleOptions = [
    { value: '', label: 'Sélectionner un rôle...' },
    { value: 'employee', label: 'Employé' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Administrateur' }
  ];

  const storeOptions = stores.map(store => ({
    value: store.id.toString(),
    label: `${store.name} (${store.city})`
  }));

  // Add a default option for stores
  const storeOptionsWithDefault = [
    { value: '', label: 'Sélectionner un magasin...' },
    ...storeOptions
  ];

  const tableColumns = [
    {
      key: 'name',
      label: 'Employé',
      render: (value, employee) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-primary-700">
              {employee.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{employee.name}</div>
            <div className="text-sm text-neutral-500">{employee.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Rôle',
      render: (value) => (
        <ModernBadge variant={getRoleBadge(value)} className="capitalize">
          {value}
        </ModernBadge>
      )
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (value) => (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'store_name',
      label: 'Magasin',
      render: (value) => (
        <div className="flex items-center">
          <Building className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Statut',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, employee) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => handleView(employee)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(employee)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(employee.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Employés</h1>
          <p className="text-neutral-600">Gérez vos employés et leurs rôles</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des employés..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter un employé
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Employés</p>
              <p className="text-2xl font-bold text-neutral-900">{employees.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Employés Actifs</p>
              <p className="text-2xl font-bold text-success-600">
                {employees.filter(emp => emp.is_active).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Managers</p>
              <p className="text-2xl font-bold text-warning-600">
                {employees.filter(emp => emp.role === 'manager').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Administrateurs</p>
              <p className="text-2xl font-bold text-danger-600">
                {employees.filter(emp => emp.role === 'admin').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Employees Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des employés</h3>
            <p className="text-sm text-neutral-600">{filteredEmployees.length} employés trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredEmployees}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier l\'employé' : 'Ajouter un employé'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom complet"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Jean Dupont"
              required
            />

            <ModernInput
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="jean.dupont@example.com"
              required
            />

            <ModernInput
              label={editingId ? "Mot de passe actuel" : "Mot de passe"}
              name="password"
              type="text"
              value={form.password}
              onChange={handleChange}
              placeholder={editingId ? "Mot de passe actuel" : "Mot de passe"}
              required={!editingId}
            />

            <ModernSelect
              label="Rôle"
              options={roleOptions}
              value={roleOptions.find(option => option.value === form.role) || roleOptions[0]}
              onChange={(selectedOption) => setForm(f => ({ ...f, role: selectedOption ? selectedOption.value : '' }))}
              placeholder="Sélectionner un rôle..."
            />

            <ModernInput
              label="Téléphone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+33 1 23 45 67 89"
            />

            <ModernSelect
              label="Magasin"
              options={storeOptionsWithDefault}
              value={storeOptionsWithDefault.find(option => option.value === form.store_id) || storeOptionsWithDefault[0]}
              onChange={(selectedOption) => setForm(f => ({ ...f, store_id: selectedOption ? selectedOption.value : '' }))}
              placeholder="Sélectionner un magasin..."
            />

            <div className="flex items-center space-x-3 pt-6">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
              />
              <label className="text-sm font-medium text-neutral-700">
                Employé actif
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* View Employee Modal */}
      <ModernModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Détails de l'employé"
        size="lg"
      >
        {selectedEmployee && (
          <div className="space-y-6">
            {/* Employee Avatar and Basic Info */}
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold text-primary-700">
                  {selectedEmployee.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-neutral-900">{selectedEmployee.name}</h3>
                <p className="text-neutral-600">{selectedEmployee.email}</p>
                <div className="flex items-center mt-2">
                  {getStatusBadge(selectedEmployee.is_active)}
                  <ModernBadge variant={getRoleBadge(selectedEmployee.role)} className="ml-2 capitalize">
                    {selectedEmployee.role}
                  </ModernBadge>
                </div>
              </div>
            </div>

            {/* Employee Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Email</p>
                    <p className="text-neutral-900">{selectedEmployee.email}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Téléphone</p>
                    <p className="text-neutral-900">{selectedEmployee.phone || 'Non renseigné'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Magasin</p>
                    <p className="text-neutral-900">{selectedEmployee.store_name || 'Non assigné'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Mot de passe</p>
                    <p className="text-neutral-900 font-mono text-sm bg-neutral-100 px-2 py-1 rounded">
                      {selectedEmployee.password_display || '••••••••'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Rôle</p>
                    <p className="text-neutral-900 capitalize">{selectedEmployee.role}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Date d'embauche</p>
                    <p className="text-neutral-900">
                      {selectedEmployee.hire_date ? new Date(selectedEmployee.hire_date).toLocaleDateString('fr-FR') : 'Non renseignée'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Statut</p>
                    <p className="text-neutral-900">
                      {selectedEmployee.is_active ? 'Actif' : 'Inactif'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-neutral-200">
              <ModernButton
                type="button"
                variant="secondary"
                onClick={() => setShowViewModal(false)}
              >
                Fermer
              </ModernButton>
              <ModernButton
                type="button"
                variant="primary"
                icon={<Edit className="w-4 h-4" />}
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(selectedEmployee);
                }}
              >
                Modifier
              </ModernButton>
            </div>
          </div>
        )}
      </ModernModal>
    </div>
  );
};

export default Employes; 